import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * ヘルプ記事の組み立て部品。
 *
 * 記事側では見た目を決めさせない。文章を書くたびに余白と文字色を選ぶと、
 * 8 枚のうち何枚かが必ずズレるので、ここに出てくる形だけで組む。
 * 色はトークン経由、識別子とコマンドは等幅、面の区切りは 1px のボーダーだけ。
 *
 * **本文の日本語は文字列リテラル (`{'…'}`) で書く。** JSX に地の文で書くと、
 * 整形で行が結合されたときにソースの改行位置がそのまま半角スペースになり、
 * 「からも コピーできます」のように語の途中が空く。リテラルなら整形しても
 * 中身が動かない (LP が同じ理由で同じ書き方をしている)。
 */

/** 記事の中の節。見出しと本文をまとめる */
export function HelpSection({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 border-t border-line pt-6 sm:pt-8">
      <h2 className="text-base font-bold">{heading}</h2>
      <div className="flex flex-col gap-4 text-sm leading-relaxed text-ink-muted">{children}</div>
    </section>
  );
}

/** 本文の段落。記事側で生の <p> を書かないための入口 */
export function P({ children }: { children: ReactNode }) {
  return <p>{children}</p>;
}

/** 本文中の強調。太字にするだけで色は変えない */
export function Strong({ children }: { children: string }) {
  return <strong className="font-bold text-ink">{children}</strong>;
}

/** 順序のない箇条書き */
export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item, index) => (
        // 表示順そのものなので index を key にしてよい
        // biome-ignore lint/suspicious/noArrayIndexKey: 上のとおり項番 = 表示順
        <li key={index} className="flex gap-3">
          <span aria-hidden className="text-ink-faint">
            —
          </span>
          <span className="flex-1">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 番号付きの手順。
 *
 * 手順を散文で書くと読まれないので、「何をするか」を太字の 1 行に切り出し、
 * 補足をその下に置く。番号は表示順そのもの。
 */
export function Steps({ items }: { items: Array<{ title: string; body?: ReactNode }> }) {
  return (
    <ol className="flex flex-col gap-5 border-l border-line pl-4 sm:pl-6">
      {items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: 手順の番号 = 表示順
        <li key={index} className="flex flex-col gap-2">
          <div className="flex gap-3">
            <span aria-hidden className="font-mono text-xs text-ink-faint">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className="flex-1 font-bold text-ink">{item.title}</span>
          </div>
          {item.body ? <div className="flex flex-col gap-3 pl-0 sm:pl-8">{item.body}</div> : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * 補足の枠。
 *
 * `alert` は「超過・要確認」だけの色なので、注意書きでも使わない。
 * 面を一段変えて罫線を太い方に振るだけで、本文との区別は付く。
 */
export function Note({ label = 'NOTE', children }: { label?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border border-line-bold bg-surface px-4 py-4 sm:px-5">
      <span className="font-mono text-[11px] tracking-wider text-ink-subtle">{label}</span>
      <div className="flex flex-col gap-2 text-[13px] leading-relaxed text-ink-muted">
        {children}
      </div>
    </div>
  );
}

/** 用語や設定項目の「名前 / 説明」の対応表 */
export function Terms({ rows }: { rows: Array<{ term: string; value: ReactNode }> }) {
  return (
    <dl className="border-t border-line">
      {rows.map((row) => (
        <div
          key={row.term}
          className="grid gap-2 border-b border-line py-4 sm:grid-cols-[11rem_1fr] sm:gap-6"
        >
          <dt className="text-sm font-bold text-ink">{row.term}</dt>
          <dd className="text-sm leading-relaxed text-ink-muted">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** よくある質問。答えが別の記事にあるものは本文からリンクで返す */
export function Faq({ items }: { items: Array<{ question: string; answer: ReactNode }> }) {
  return (
    <div className="flex flex-col divide-y divide-line border-y border-line">
      {items.map((item) => (
        <div key={item.question} className="flex flex-col gap-2 py-5">
          <h3 className="text-sm font-bold text-ink">{item.question}</h3>
          <div className="flex flex-col gap-2 text-sm leading-relaxed text-ink-muted">
            {item.answer}
          </div>
        </div>
      ))}
    </div>
  );
}

/** 画面の名前。押すとその画面へ移動する (未ログインならログインへ回る) */
export function Screen({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      className="border-b border-line-bold text-ink transition-colors hover:border-brand-700 hover:text-brand-700"
    >
      {children}
    </Link>
  );
}

/** 別の記事への参照 */
export function SeeAlso({ slug, children }: { slug: string; children: string }) {
  return (
    <Link
      to={`/help/${slug}`}
      className="border-b border-line-bold text-ink transition-colors hover:border-brand-700 hover:text-brand-700"
    >
      {children}
    </Link>
  );
}

/** 設定値・カラム名・コマンドなど、本文に混ぜる識別子 */
export function Code({ children }: { children: string }) {
  return (
    <code className="bg-surface px-1.5 py-0.5 font-mono text-xs text-ink-secondary">
      {children}
    </code>
  );
}
